import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import { loopsAPI } from '../utils/supabase'
import { handleRealtimeLoopEvent } from './reducers/realtimeReducers'
import { addNotification } from './notificationSlice'
import { deleteTask } from './reducers/tasksReducers'

// Async thunks
export const fetchLoops = createAsyncThunk(
  'loops/fetch',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await loopsAPI.getAllLoops(userId)
      if (error) throw error
      return data || []
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createLoop = createAsyncThunk(
  'loops/create',
  async ({ userId, loopData }, { rejectWithValue, dispatch }) => {
    // Generate optimistic ID for immediate UI update
    const optimisticId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    // TIMEZONE NOTE: Optimistic timestamps below use new Date().toISOString() which
    // is intentional for temporary display only. These are NEVER persisted - the server
    // generates proper TIMESTAMPTZ values that replace these within milliseconds when
    // replaceOptimisticLoop is called. Loops don't have date-only fields that require
    // user timezone conversion (unlike task due_date). This approach is acceptable per
    // CLAUDE.md since timestamps are server-authoritative and immediately replaced.
    const optimisticLoop = {
      id: optimisticId,
      user_id: userId,
      note: loopData.note,
      color: loopData.color || 'yellow',
      status: 'open',
      task_id: loopData.task_id || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      closed_at: null,
      _isOptimistic: true
    }

    try {
      // Optimistic add - immediately show in UI
      dispatch({ type: 'loops/addLoopOptimistic', payload: optimisticLoop })

      const { data, error } = await loopsAPI.createLoop(userId, loopData)
      if (error) throw error

      // Replace optimistic loop with real one
      dispatch({
        type: 'loops/replaceOptimisticLoop',
        payload: { optimisticId, realLoop: data }
      })

      return { realLoop: data, optimisticId, _wasOptimistic: true }
    } catch (error) {
      // Remove optimistic loop on error
      dispatch({ type: 'loops/removeLoopOptimistic', payload: optimisticId })
      dispatch(addNotification({
        id: `loop-error-${Date.now()}`,
        type: 'error',
        message: 'Failed to create loop. Please try again.'
      }))
      return rejectWithValue(error.message)
    }
  }
)

export const updateLoop = createAsyncThunk(
  'loops/update',
  async ({ loopId, updates }, { rejectWithValue, dispatch, getState }) => {
    // Get current loop state for potential rollback
    const state = getState()
    const originalLoop = state.loops.loops.find(loop => loop.id === loopId)

    if (!originalLoop) {
      return rejectWithValue('Loop not found')
    }

    try {
      // Optimistic update - immediately reflect changes (see TIMEZONE NOTE in createLoop)
      dispatch({
        type: 'loops/updateLoopOptimistic',
        payload: { loopId, updates: { ...updates, updated_at: new Date().toISOString() } }
      })

      const { data, error } = await loopsAPI.updateLoop(loopId, updates)
      if (error) throw error
      return data
    } catch (error) {
      // Rollback on error
      dispatch({
        type: 'loops/rollbackLoopUpdate',
        payload: { loopId, previousData: originalLoop }
      })
      dispatch(addNotification({
        id: `loop-error-${Date.now()}`,
        type: 'error',
        message: 'Failed to update loop. Please try again.'
      }))
      return rejectWithValue(error.message)
    }
  }
)

export const closeLoop = createAsyncThunk(
  'loops/close',
  async (loopId, { rejectWithValue, dispatch, getState }) => {
    // Get current loop state for potential rollback
    const state = getState()
    const originalLoop = state.loops.loops.find(loop => loop.id === loopId)

    if (!originalLoop) {
      return rejectWithValue('Loop not found')
    }

    try {
      // Optimistic update - immediately show as closed (see TIMEZONE NOTE in createLoop)
      dispatch({
        type: 'loops/updateLoopOptimistic',
        payload: { loopId, updates: { status: 'closed', closed_at: new Date().toISOString() } }
      })

      const { data, error } = await loopsAPI.closeLoop(loopId)
      if (error) throw error
      return data
    } catch (error) {
      // Rollback on error
      dispatch({
        type: 'loops/rollbackLoopUpdate',
        payload: { loopId, previousData: originalLoop }
      })
      dispatch(addNotification({
        id: `loop-error-${Date.now()}`,
        type: 'error',
        message: 'Failed to close loop. Please try again.'
      }))
      return rejectWithValue(error.message)
    }
  }
)

export const reopenLoop = createAsyncThunk(
  'loops/reopen',
  async (loopId, { rejectWithValue, dispatch, getState }) => {
    // Get current loop state for potential rollback
    const state = getState()
    const originalLoop = state.loops.loops.find(loop => loop.id === loopId)

    if (!originalLoop) {
      return rejectWithValue('Loop not found')
    }

    try {
      // Optimistic update - immediately show as open
      dispatch({
        type: 'loops/updateLoopOptimistic',
        payload: { loopId, updates: { status: 'open', closed_at: null } }
      })

      const { data, error } = await loopsAPI.reopenLoop(loopId)
      if (error) throw error
      return data
    } catch (error) {
      // Rollback on error
      dispatch({
        type: 'loops/rollbackLoopUpdate',
        payload: { loopId, previousData: originalLoop }
      })
      dispatch(addNotification({
        id: `loop-error-${Date.now()}`,
        type: 'error',
        message: 'Failed to reopen loop. Please try again.'
      }))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteLoop = createAsyncThunk(
  'loops/delete',
  async (loopId, { rejectWithValue, dispatch, getState }) => {
    // Get current loop state for potential restore on failure
    const state = getState()
    const loopToDelete = state.loops.loops.find(loop => loop.id === loopId)

    if (!loopToDelete) {
      return rejectWithValue('Loop not found')
    }

    try {
      // Optimistic delete - immediately remove from UI
      dispatch({ type: 'loops/removeLoopOptimistic', payload: loopId })

      const { error } = await loopsAPI.deleteLoop(loopId)
      if (error) throw error
      return loopId
    } catch (error) {
      // Restore on error
      dispatch({ type: 'loops/restoreLoop', payload: loopToDelete })
      dispatch(addNotification({
        id: `loop-error-${Date.now()}`,
        type: 'error',
        message: 'Failed to delete loop. Please try again.'
      }))
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  loops: [],
  loading: false,
  error: null,
  initialized: false,
  // Track pending optimistic IDs to prevent duplicate inserts from realtime events
  _pendingOptimisticIds: []
}

const loopsSlice = createSlice({
  name: 'loops',
  initialState,
  reducers: {
    setLoops: (state, action) => {
      state.loops = action.payload
      state.initialized = true
    },
    clearLoops: (state) => {
      state.loops = []
      state.initialized = false
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    // Optimistic update support
    updateLoopOptimistic: (state, action) => {
      const { loopId, updates } = action.payload
      const index = state.loops.findIndex(loop => loop.id === loopId)
      if (index !== -1) {
        state.loops[index] = { ...state.loops[index], ...updates }
      }
    },
    // Rollback optimistic updates on failure
    rollbackLoopUpdate: (state, action) => {
      const { loopId, previousData } = action.payload
      const index = state.loops.findIndex(loop => loop.id === loopId)
      if (index !== -1) {
        state.loops[index] = previousData
      }
    },
    // Optimistic add for instant UI feedback on create
    addLoopOptimistic: (state, action) => {
      state.loops.unshift(action.payload)
      // Track the optimistic ID to prevent duplicate inserts from realtime
      if (action.payload.id) {
        state._pendingOptimisticIds.push(action.payload.id)
      }
    },
    // Optimistic remove for instant UI feedback on delete
    removeLoopOptimistic: (state, action) => {
      state.loops = state.loops.filter(loop => loop.id !== action.payload)
      // Also clear from pending IDs in case this was a failed create
      state._pendingOptimisticIds = state._pendingOptimisticIds.filter(id => id !== action.payload)
    },
    // Restore a loop on delete failure
    restoreLoop: (state, action) => {
      state.loops.unshift(action.payload)
    },
    // Replace optimistic loop with real one from server
    replaceOptimisticLoop: (state, action) => {
      const { optimisticId, realLoop } = action.payload
      const index = state.loops.findIndex(loop => loop.id === optimisticId)
      if (index !== -1) {
        state.loops[index] = realLoop
      }
      // Remove from pending optimistic IDs and add real ID to prevent realtime duplicate
      state._pendingOptimisticIds = state._pendingOptimisticIds.filter(id => id !== optimisticId)
      if (realLoop?.id) {
        state._pendingOptimisticIds.push(realLoop.id)
      }
    },
    // Clean up a pending optimistic ID after realtime event window has passed
    clearPendingOptimisticId: (state, action) => {
      state._pendingOptimisticIds = state._pendingOptimisticIds.filter(id => id !== action.payload)
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Loops
      .addCase(fetchLoops.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLoops.fulfilled, (state, action) => {
        state.loading = false
        state.loops = action.payload
        state.initialized = true
      })
      .addCase(fetchLoops.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Create Loop - optimistic add handled in thunk, fulfilled syncs server data
      .addCase(createLoop.pending, (state) => {
        state.error = null
      })
      .addCase(createLoop.fulfilled, (state, action) => {
        // Optimistic add and replace handled in thunk via dispatched actions
        // This fulfilled handler is a no-op for optimistic updates
        // (replaceOptimisticLoop already replaced the temp loop with real data)
      })
      .addCase(createLoop.rejected, (state, action) => {
        state.error = action.payload
      })

      // Update Loop - optimistic update handled in thunk, fulfilled syncs server data
      .addCase(updateLoop.pending, (state) => {
        state.error = null
      })
      .addCase(updateLoop.fulfilled, (state, action) => {
        // Sync server data to ensure consistency
        const index = state.loops.findIndex(loop => loop.id === action.payload.id)
        if (index !== -1) {
          state.loops[index] = action.payload
        }
      })
      .addCase(updateLoop.rejected, (state, action) => {
        state.error = action.payload
      })

      // Close Loop - optimistic update handled in thunk, fulfilled syncs server data
      .addCase(closeLoop.pending, (state) => {
        state.error = null
      })
      .addCase(closeLoop.fulfilled, (state, action) => {
        // Sync server data to ensure consistency
        const index = state.loops.findIndex(loop => loop.id === action.payload.id)
        if (index !== -1) {
          state.loops[index] = action.payload
        }
      })
      .addCase(closeLoop.rejected, (state, action) => {
        state.error = action.payload
      })

      // Reopen Loop - optimistic update handled in thunk, fulfilled syncs server data
      .addCase(reopenLoop.pending, (state) => {
        state.error = null
      })
      .addCase(reopenLoop.fulfilled, (state, action) => {
        // Sync server data to ensure consistency
        const index = state.loops.findIndex(loop => loop.id === action.payload.id)
        if (index !== -1) {
          state.loops[index] = action.payload
        }
      })
      .addCase(reopenLoop.rejected, (state, action) => {
        state.error = action.payload
      })

      // Delete Loop - optimistic delete handled in thunk
      .addCase(deleteLoop.pending, (state) => {
        state.error = null
      })
      .addCase(deleteLoop.fulfilled, (state, action) => {
        // Optimistic delete already removed the loop in thunk
        // This is a no-op - loop already removed via removeLoopOptimistic
      })
      .addCase(deleteLoop.rejected, (state, action) => {
        state.error = action.payload
      })

      // When a task is deleted, clear the tasks field from any loops that reference it
      .addCase(deleteTask.fulfilled, (state, action) => {
        const deletedTaskId = action.payload
        state.loops.forEach(loop => {
          if (loop.task_id === deletedTaskId || loop.tasks?.id === deletedTaskId) {
            loop.task_id = null
            loop.tasks = null
          }
        })
      })

      // Realtime loop events (cross-tab sync)
      .addCase(handleRealtimeLoopEvent.fulfilled, (state, action) => {
        if (!action.payload) return

        const { type, record } = action.payload

        switch (type) {
          case 'INSERT': {
            // Skip if this loop ID is in our pending optimistic IDs (we just created it)
            if (state._pendingOptimisticIds.includes(record.id)) {
              if (import.meta.env.DEV) {
                console.log('[Realtime] Skipping own optimistic loop INSERT:', record.id)
              }
              // Remove from pending IDs now that we've seen the realtime event
              state._pendingOptimisticIds = state._pendingOptimisticIds.filter(id => id !== record.id)
              break
            }

            // Add new loop (avoid duplicates by ID)
            if (!state.loops.find(loop => loop.id === record.id)) {
              state.loops.unshift(record)
            }
            break
          }

          case 'UPDATE': {
            // Update existing loop
            const loopIndex = state.loops.findIndex(loop => loop.id === record.id)
            if (loopIndex !== -1) {
              state.loops[loopIndex] = { ...state.loops[loopIndex], ...record }
            }
            break
          }

          case 'DELETE': {
            if (record?.id) {
              state.loops = state.loops.filter(loop => loop.id !== record.id)
            }
            break
          }
        }
      })
  }
})

export const {
  setLoops,
  clearLoops,
  clearError,
  updateLoopOptimistic,
  rollbackLoopUpdate,
  addLoopOptimistic,
  removeLoopOptimistic,
  restoreLoop,
  replaceOptimisticLoop,
  clearPendingOptimisticId
} = loopsSlice.actions

// Selectors
export const selectLoops = (state) => state.loops.loops
export const selectLoopsLoading = (state) => state.loops.loading
export const selectLoopsError = (state) => state.loops.error
export const selectLoopsInitialized = (state) => state.loops.initialized

// Open loops only
export const selectOpenLoops = createSelector(
  [selectLoops],
  (loops) => loops.filter(loop => loop.status === 'open')
)

// Closed loops only
export const selectClosedLoops = createSelector(
  [selectLoops],
  (loops) => loops.filter(loop => loop.status === 'closed')
)

// Count of open loops
export const selectOpenLoopsCount = createSelector(
  [selectOpenLoops],
  (loops) => loops.length
)

export default loopsSlice.reducer
